import { useCallback, useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { useHistory } from "react-router-dom";
import { TbCirclePlus, TbKey, TbRefresh } from "react-icons/tb";
import {
  GRID_HISTORY_COLS_4,
  GridHistoryCell,
  GridHistoryContainer,
  GridHistoryHeader,
  GridHistoryRow,
} from "../../../Components/Molecules/GridHistory";
import { useAuth } from "../../../Contexts/AuthContext";
import { useBaasInstance } from "../../../Contexts/BaasInstanceContext";
import { useActiveIbAccount } from "../../../Hooks/useActiveIbAccount";
import { useToast } from "../../../Hooks/toast";
import { fetchOperatingAccounts } from "../../../Services/operatingAccountsApi";
import { isBaasExitRailVirtualAccount } from "../../../Services/baasVirtualShells";
import { BAAS_VIRTUAL_ACCOUNTS_PATH } from "../BaasConsole/constants";
import { BaasAccountPixKeysModal } from "../BaasConsole/Accounts/components/BaasAccountPixKeysModal";
import { BaasVirtualAccountCreateModal } from "../BaasConsole/Accounts/components/BaasVirtualAccountCreateModal";
import { filterVirtualAccountsByActiveEntity, resolveAccountEntityId } from "./virtualAccountsEntityScope";

const EXIT_RAIL_TYPE_TOKENS = new Set([
  "VIRTUAL",
  "WALLET",
  "OTC",
  "OTC_VIRTUAL",
  "SEGREGATION_WALLET",
]);

const EXIT_RAIL_ROLE_TOKENS = new Set([
  "otc_virtual",
  "obr_virtual",
  "segregation_wallet",
  "wallet",
  "otc",
]);

const isIbVirtualAccount = (account) => {
  if (!account) return false;

  const rawType = String(account.raw?.account_type || account.displayType || "")
    .trim()
    .toUpperCase();
  const role = String(account.role || account.raw?.role || "")
    .trim()
    .toLowerCase();

  if (EXIT_RAIL_TYPE_TOKENS.has(rawType) || EXIT_RAIL_ROLE_TOKENS.has(role)) {
    return true;
  }
  if (account.isVirtual === true || account.raw?.is_virtual === true) {
    if (isBaasExitRailVirtualAccount(account) || isBaasExitRailVirtualAccount(account.raw)) {
      return true;
    }
    // API may mark exit-rail as is_virtual without WALLET/OTC name literals.
    if (rawType === "VIRTUAL" || EXIT_RAIL_ROLE_TOKENS.has(role)) {
      return true;
    }
  }
  return (
    isBaasExitRailVirtualAccount(account) || isBaasExitRailVirtualAccount(account.raw)
  );
};

/**
 * Carteira home surface for exit-rail virtuals (WALLET/OTC).
 * Create reuses BaaS APIs when an instance is selected; Pix keys open IB Minhas chaves
 * (or console modal when available). Hidden from sidebar when active account is VIRTUAL.
 */
export const VirtualAccountsPage = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { user, hasPermission } = useAuth();
  const canCreateVirtual = hasPermission("virtualaccountscreate");
  const { selectedInstance } = useBaasInstance();
  const {
    isExitRailVirtual,
    entityId: activeEntityId,
    loading: activeAccountLoading,
  } = useActiveIbAccount();
  const canAccessConsole = user?.canAccessConsole === true;

  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scopeHint, setScopeHint] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [pixAccount, setPixAccount] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  // Contas virtuais is matriz/operacional only — redirect when active account is VIRTUAL.
  useEffect(() => {
    if (activeAccountLoading) return;
    if (isExitRailVirtual) {
      history.replace("/");
    }
  }, [activeAccountLoading, history, isExitRailVirtual]);

  const refresh = useCallback(async () => {
    // Wait for active account entity before filtering — avoids empty flash / wrong scope.
    if (activeAccountLoading) return;

    setLoading(true);
    setScopeHint(null);
    try {
      const next = await fetchOperatingAccounts();
      const list = Array.isArray(next) ? next : [];
      if (!String(activeEntityId || "").trim()) {
        // Active account may itself be an exit-rail VIRTUAL still missing entity_id.
        // Show those unscoped VIRTUAL rows instead of a hard empty / scope error.
        const unscoped = list.filter(
          (account) => isIbVirtualAccount(account) && !resolveAccountEntityId(account),
        );
        setAccounts(unscoped);
        setScopeHint(
          unscoped.length > 0 ? null : intl.get("virtualAccounts.entityScopeRequired"),
        );
        return;
      }
      setAccounts(
        filterVirtualAccountsByActiveEntity(list, activeEntityId, isIbVirtualAccount),
      );
    } catch (error) {
      setAccounts([]);
      addToast({
        type: "error",
        title: error?.message || intl.get("virtualAccounts.loadError"),
      });
    } finally {
      setLoading(false);
    }
  }, [addToast, activeEntityId, activeAccountLoading]);

  useEffect(() => {
    refresh();
  }, [refresh, reloadToken]);

  const canCreate = Boolean(
    canCreateVirtual
      && canAccessConsole
      && selectedInstance?.baasInstanceId
      && !isExitRailVirtual
      && activeEntityId,
  );
  const showCreateCta = canCreateVirtual && !isExitRailVirtual;
  const canOpenBaasPix = Boolean(canAccessConsole && selectedInstance?.baasInstanceId);

  const rows = useMemo(() => accounts, [accounts]);

  // Avoid flashing page content while redirecting away from VIRTUAL active account.
  if (!activeAccountLoading && isExitRailVirtual) {
    return null;
  }

  const handleCreateClick = () => {
    if (!canCreateVirtual) return;
    if (isExitRailVirtual) {
      addToast({
        type: "error",
        title: intl.get("virtualAccounts.createBlockedOnVirtual"),
      });
      return;
    }
    if (canCreate) {
      setCreateOpen(true);
      return;
    }
    if (canAccessConsole) {
      const qs = activeEntityId
        ? `?create=1&entityId=${encodeURIComponent(activeEntityId)}`
        : "?create=1";
      history.push(`${BAAS_VIRTUAL_ACCOUNTS_PATH}${qs}`);
      return;
    }
    addToast({
      type: "error",
      title: intl.get("virtualAccounts.createRequiresConsole"),
    });
  };

  const handlePixKeys = (account) => {
    if (canOpenBaasPix) {
      setPixAccount({
        id: account.id,
        entityId: account.entityId || account.raw?.entity_id || "",
        entityName: account.label,
        type: String(account.raw?.account_type || account.role || "wallet")
          .trim()
          .toLowerCase(),
        name: account.label,
        alias: account.label,
        isVirtual: true,
        is_virtual: true,
        raw: account.raw,
      });
      return;
    }
    // IB path — Minhas chaves on the active account (switch account to manage that WALLET).
    history.push("/pix/keys");
  };

  return (
    <div className="virtual-accounts-page min-w-0 space-y-5">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-brand-dark dark:text-gray-100">
            {intl.get("nav.virtualAccounts")}
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {intl.get("virtualAccounts.subtitle")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-outline w-auto inline-flex items-center gap-2"
            onClick={() => setReloadToken((value) => value + 1)}
            disabled={loading}
          >
            <TbRefresh className="h-4 w-4" aria-hidden />
            {intl.get("baas.pix.refresh")}
          </button>
          {showCreateCta ? (
            <button
              type="button"
              className="btn-primary w-auto inline-flex items-center gap-2"
              onClick={handleCreateClick}
            >
              <TbCirclePlus className="h-4 w-4" aria-hidden />
              {intl.get("baas.accounts.virtualCreate.submit")}
            </button>
          ) : null}
        </div>
      </header>

      {isExitRailVirtual ? (
        <p
          role="status"
          className="rounded-xl border border-[#e7a73f]/35 bg-[#e7a73f]/10 px-3 py-2 text-xs text-[#7a5418] dark:border-brand-gold-mid/40 dark:bg-brand-gold-mid/10 dark:text-brand-gold-mid"
        >
          {intl.get("virtualAccounts.activeVirtualBanner")}
        </p>
      ) : (
        <p className="rounded-xl border border-[#e7a73f]/35 bg-[#e7a73f]/10 px-3 py-2 text-xs text-[#7a5418] dark:border-brand-gold-mid/40 dark:bg-brand-gold-mid/10 dark:text-brand-gold-mid">
          {intl.get("virtualAccounts.pixFlowHint")}
        </p>
      )}

      <div className="app-card overflow-hidden">
        {loading || activeAccountLoading ? (
          <div className="app-empty-state m-4">{intl.get("virtualAccounts.loading")}</div>
        ) : rows.length === 0 ? (
          <div className="m-4 space-y-3 text-center">
            <div className="app-empty-state">
              {scopeHint || intl.get("virtualAccounts.empty")}
            </div>
            {showCreateCta && !scopeHint ? (
              <button
                type="button"
                className="btn-primary w-auto inline-flex items-center gap-2"
                onClick={handleCreateClick}
              >
                <TbCirclePlus className="h-4 w-4" aria-hidden />
                {intl.get("baas.accounts.virtualCreate.submit")}
              </button>
            ) : null}
          </div>
        ) : (
          <GridHistoryContainer gridCols={GRID_HISTORY_COLS_4}>
            <GridHistoryHeader colSpan={4}>
              <span>{intl.get("baas.accounts.columns.account")}</span>
              <span>{intl.get("baas.accounts.columns.type")}</span>
              <span>{intl.get("baas.accounts.columns.accountNumber")}</span>
              <span className="text-right">{intl.get("baas.accounts.columns.actions")}</span>
            </GridHistoryHeader>
            {rows.map((account) => {
              const rawType = String(account.raw?.account_type || account.role || "WALLET")
                .trim()
                .toUpperCase();
              // OTC is internal — user-facing label is always WALLET / conta virtual.
              const typeLabel =
                rawType === "OTC" || rawType === "OTC_VIRTUAL"
                  ? "WALLET"
                  : rawType;
              return (
                <GridHistoryRow key={account.id} colSpan={4}>
                  <GridHistoryCell label={intl.get("baas.accounts.columns.account")}>
                    <p className="truncate text-sm font-semibold text-brand-dark dark:text-gray-100">
                      {account.label}
                    </p>
                    <p className="truncate font-mono text-xs text-gray-500 dark:text-gray-400">
                      {account.id}
                    </p>
                  </GridHistoryCell>
                  <GridHistoryCell label={intl.get("baas.accounts.columns.type")}>
                    <p className="text-sm text-gray-600 dark:text-gray-300">{typeLabel}</p>
                  </GridHistoryCell>
                  <GridHistoryCell label={intl.get("baas.accounts.columns.accountNumber")}>
                    <p className="font-mono text-xs text-gray-600 dark:text-gray-300">
                      {account.accountNumber || "—"}
                    </p>
                  </GridHistoryCell>
                  <GridHistoryCell label={intl.get("baas.accounts.columns.actions")} align="right">
                    <button
                      type="button"
                      className="btn-outline w-auto !text-xs inline-flex items-center gap-1.5"
                      onClick={() => handlePixKeys(account)}
                      title={intl.get("virtualAccounts.pixKeysHint")}
                    >
                      <TbKey className="h-3.5 w-3.5" aria-hidden />
                      {intl.get("baas.accounts.quickActions.pixKeys")}
                    </button>
                  </GridHistoryCell>
                </GridHistoryRow>
              );
            })}
          </GridHistoryContainer>
        )}
      </div>

      {canCreate ? (
        <BaasVirtualAccountCreateModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          initialEntityId={activeEntityId || ""}
          onCreated={() => {
            setReloadToken((value) => value + 1);
            setCreateOpen(false);
          }}
        />
      ) : null}

      {pixAccount ? (
        <BaasAccountPixKeysModal
          open={Boolean(pixAccount)}
          onClose={() => setPixAccount(null)}
          accountId={pixAccount.id}
          account={pixAccount}
        />
      ) : null}
    </div>
  );
};

export default VirtualAccountsPage;
